<template>
  <div
    :id="`cta-card-${randomId}`"
    :data-testid="dataTestid"
    :class="props.class || twMerge('flex items-center justify-center gap-2 flex-col md:flex-row', appendClass)"
  >
    <slot name="content">
      <TsTypography
        v-if="props.content"
        append-class="text-center lg:text-left mb-0 font-bold shrink max-md:mb-2 md:mr-2 text-idle-black"
      >
        {{ content }}
      </TsTypography>
    </slot>
    <div class="flex gap-2 min-w-max">
      <slot name="cta-buttons">
        <NuxtLink
          @click="logDownload('App Store')"
          :data-testid="dataTestid + '-download-apple-app'"
          :to="appleStoreLink"
          target="_blank"
          class="cursor-pointer flex items-center gap-0.5 border rounded-sm border-gray-500 p-2 bg-idle-black text-idle-white apple-footer-download"
        >
          <TsIcon name="ic:sharp-apple" size="24" class="mr-1 shrink-0" />
          <div class="text-left">
            <p class="leading-none text-[8px]">
              {{ useTranslation('downloadOn', 'Downloaden in de') }}
            </p>
            <p class="leading-tight tracking-wider text-sm font-semibold">App Store</p>
          </div>
        </NuxtLink>

        <NuxtLink
          @click="logDownload('Google Play')"
          :data-testid="dataTestid + '-download-google-play-app'"
          :to="googlePlayLink"
          target="_blank"
          class="cursor-pointer flex items-center gap-0.5 border rounded-sm border-gray-500 p-2 bg-idle-black text-idle-white android-footer-download"
        >
          <TsImage class="size-6 mr-1" src="/icons/google-play.svg" alt="Google Play" loading="lazy" />
          <div class="text-left">
            <p class="leading-none text-[8px]">
              {{ useTranslation('getItOn', 'DOWNLOAD VIA') }}
            </p>
            <p class="leading-tight tracking-wider text-sm font-semibold">Google Play</p>
          </div>
        </NuxtLink>
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { twMerge } from "tailwind-merge";
import { onMounted, onBeforeUnmount, ref } from "vue";
import { useRandomUUID } from "~/composables/useRandomUUID";
import { useTranslation } from "~/composables/useTranslation";  

type Props = {
  content?: string;
  appendClass?: string;
  appleStoreLink?: string;
  googlePlayStoreLink?: string;
  class?: string;
  dataTestid?: string;
};

const props = defineProps<Props>();
const randomId = ref("");

onMounted(() => {
  randomId.value = useRandomUUID();
});

onBeforeUnmount(() => {
  // Clean up resources to prevent memory leaks
  randomId.value = "";
});

const config = useRuntimeConfig();
const appRegion = config.public.appRegion;
const isBelgium = appRegion === "BE";

const appleStoreLink = props.appleStoreLink || (
  isBelgium
    ? "https://apps.apple.com/app/toolstation-be/id1619951316"
    : "https://apps.apple.com/nl/app/toolstation-bouwen-klussen/id1619948462"
);

const googlePlayLink = props.googlePlayStoreLink || (
  isBelgium
    ? "https://play.google.com/store/apps/details?id=com.toolstation.mobile_app.be&pli=1"
    : "https://play.google.com/store/apps/details?id=com.toolstation.mobile_app.nl"
);

const logDownload = (platform: string) => {
  logEvent({
    options: {
      eventAction: "click",
      eventProperties: {
        platform: platform,
      },
      eventInteraction: true,
      eventLabel: `user tryed download from ${platform}`,
      eventName: "trackDownloadApp",
    },
  });
};
</script>
